import {
  BILLING_CYCLE_LABELS,
  FEATURE_KEYS,
  FEATURE_LABELS,
  formatLimit,
  LIMIT_KEYS,
  LIMIT_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
  type BillingCycle,
  type BillingOverviewDto,
  type PlanDto,
} from "@iagenix/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, CreditCard, ExternalLink, Minus } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errorMessage } from "@/lib/api";
import { billingApi, billingQueries, brl, shortDate } from "@/lib/billing-api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/cobranca")({
  validateSearch: z.object({ checkout: z.string().optional() }),
  component: CobrancaPage,
});

function statusVariant(status: BillingOverviewDto["entitlements"]["status"]) {
  switch (status) {
    case "active":
      return "success" as const;
    case "trialing":
      return "secondary" as const;
    case "past_due":
    case "incomplete":
      return "warning" as const;
    case "canceled":
      return "destructive" as const;
  }
}

function CobrancaPage() {
  const { workspaceId } = Route.useParams();
  const { checkout } = Route.useSearch();
  const overview = useQuery(billingQueries.overview(workspaceId));

  useEffect(() => {
    if (checkout === "sucesso") toast.success("Pagamento recebido. A assinatura é ativada assim que o Stripe confirmar.");
    if (checkout === "cancelado") toast.info("Checkout cancelado. Nada foi cobrado.");
  }, [checkout]);

  if (overview.isPending) return <LoadingState rows={5} />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const o = overview.data;
  return (
    <>
      <PageHeader
        title="Plano e cobrança"
        description="Plano atual, uso do workspace, assinatura e faturas."
        actions={
          o.mode === "mock" ? (
            <Badge variant="warning" title="BILLING_MODE=mock: nada é cobrado de verdade.">
              Cobrança simulada
            </Badge>
          ) : null
        }
      />
      <div className="space-y-6">
        {o.entitlements.access.readOnly ? (
          <Alert variant="destructive">
            <AlertTitle>Modo leitura</AlertTitle>
            <AlertDescription>{o.entitlements.access.message}</AlertDescription>
          </Alert>
        ) : null}
        <CurrentPlan workspaceId={workspaceId} o={o} />
        <UsageCard o={o} />
        <PlansCard workspaceId={workspaceId} o={o} />
        <InvoicesCard o={o} />
      </div>
    </>
  );
}

function CurrentPlan({ workspaceId, o }: { workspaceId: string; o: BillingOverviewDto }) {
  const portal = useMutation({
    mutationFn: () => billingApi.portal(workspaceId),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const e = o.entitlements;
  const sub = o.subscription;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-lg">Plano {e.plan.name}</CardTitle>
          <Badge variant={statusVariant(e.status)}>{SUBSCRIPTION_STATUS_LABELS[e.status]}</Badge>
          {e.status !== "trialing" ? <Badge variant="outline">{BILLING_CYCLE_LABELS[e.cycle]}</Badge> : null}
        </div>
        <CardDescription>
          {e.status === "trialing"
            ? e.access.trialDaysLeft !== null
              ? `Teste grátis até ${shortDate(e.trialEndsAt)} (${e.access.trialDaysLeft === 1 ? "falta 1 dia" : `faltam ${e.access.trialDaysLeft} dias`}). Nenhum cartão foi pedido.`
              : `O teste grátis acabou em ${shortDate(e.trialEndsAt)}. Assine um plano para sair do modo leitura.`
            : e.status === "past_due"
              ? `O último pagamento falhou. Tolerância até ${shortDate(e.access.graceEndsAt)}; depois disso o workspace fica em modo leitura.`
              : e.status === "incomplete"
                ? "Aguardando a confirmação do primeiro pagamento (o boleto compensa em até 1 dia útil)."
                : e.status === "canceled"
                  ? "Assinatura cancelada."
                  : sub.cancelAtPeriodEnd
                    ? `Cancelamento agendado: a assinatura vale até ${shortDate(sub.currentPeriodEnd)} e não será renovada.`
                    : `Renova em ${shortDate(sub.currentPeriodEnd)}.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-3">
        <Info label="Próximo pagamento" value={sub.nextPaymentCents !== null ? `${brl(sub.nextPaymentCents)} em ${shortDate(sub.nextPaymentAt)}` : "—"} />
        <Info
          label="Forma de pagamento"
          value={
            sub.paymentMethod
              ? sub.paymentMethod.type === "card"
                ? `Cartão ${sub.paymentMethod.brand ?? ""} final ${sub.paymentMethod.last4 ?? "----"}`
                : sub.paymentMethod.type === "boleto"
                  ? "Boleto bancário"
                  : (sub.paymentMethod.brand ?? "Outra")
              : "—"
          }
        />
        <Info
          label="Desconto"
          value={
            sub.discount
              ? `${sub.discount.code ?? "Cupom"}: ${sub.discount.percentOff ? `${sub.discount.percentOff}%` : brl(sub.discount.amountOffCents)}${sub.discount.endsAt ? ` até ${shortDate(sub.discount.endsAt)}` : ""}`
              : "—"
          }
        />
      </CardContent>
      <CardFooter className="flex flex-wrap gap-2">
        {sub.hasStripeSubscription ? (
          <Button onClick={() => portal.mutate()} loading={portal.isPending} disabled={!o.canManage}>
            <CreditCard />
            Gerenciar assinatura
          </Button>
        ) : null}
        {o.mode === "mock" && !sub.hasStripeSubscription && o.canManage ? (
          <Button variant="outline" onClick={() => portal.mutate()} loading={portal.isPending}>
            Simulações de teste
          </Button>
        ) : null}
        {!o.canManage ? <p className="text-sm text-muted-foreground">Só o Dono do workspace assina, troca de plano ou cancela.</p> : null}
        {sub.hasStripeSubscription ? (
          <p className="text-xs text-muted-foreground">
            No portal você troca de plano, atualiza o cartão, cancela e baixa as faturas.
          </p>
        ) : null}
      </CardFooter>
    </Card>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}

function UsageCard({ o }: { o: BillingOverviewDto }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Uso do plano</CardTitle>
        <CardDescription>Os limites valem no servidor: ao chegar no teto, a ação é recusada com a opção de fazer upgrade.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5 sm:grid-cols-2">
        {LIMIT_KEYS.map((key) => {
          const limit = o.entitlements.limits[key];
          const used = o.usage[key];
          const pct = limit === null ? 0 : limit === 0 ? 100 : Math.min(100, Math.round((used / limit) * 100));
          const notIncluded = limit === 0 && used === 0;
          const full = limit !== null && used >= limit && !notIncluded;
          return (
            <div key={key} className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span>{LIMIT_LABELS[key]}</span>
                <span className={cn("tabular-nums", full ? "font-medium text-destructive" : "text-muted-foreground")}>
                  {notIncluded ? "Não inclui no plano" : `${new Intl.NumberFormat("pt-BR").format(used)} / ${formatLimit(limit)}`}
                </span>
              </div>
              <div
                className="h-2 overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-label={LIMIT_LABELS[key]}
                aria-valuemin={0}
                aria-valuemax={limit ?? undefined}
                aria-valuenow={used}
              >
                <div
                  className={cn("h-full rounded-full", full ? "bg-destructive" : pct >= 80 ? "bg-amber-500" : "bg-primary")}
                  style={{ width: `${notIncluded ? 0 : limit === null ? Math.min(100, used > 0 ? 8 : 0) : pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function PlansCard({ workspaceId, o }: { workspaceId: string; o: BillingOverviewDto }) {
  const [cycle, setCycle] = useState<BillingCycle>(o.entitlements.status === "trialing" ? "mensal" : o.entitlements.cycle);
  const [coupon, setCoupon] = useState("");
  const subscribing = o.subscription.hasStripeSubscription;
  const checkout = useMutation({
    mutationFn: (plan: PlanDto) =>
      billingApi.checkout(workspaceId, { planCode: plan.code, cycle, ...(coupon.trim() ? { coupon: coupon.trim() } : {}) }),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const portal = useMutation({
    mutationFn: () => billingApi.portal(workspaceId),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Planos</CardTitle>
            <CardDescription>Pagamento em reais por {o.paymentMethods.join(" ou ").toLowerCase()}.</CardDescription>
          </div>
          <div className="inline-flex rounded-lg bg-muted p-1" role="group" aria-label="Ciclo de cobrança">
            {(["mensal", "anual"] as const).map((c) => (
              <button
                key={c}
                type="button"
                aria-pressed={cycle === c}
                onClick={() => setCycle(c)}
                className={cn("rounded-md px-3 py-1 text-sm font-medium", cycle === c ? "bg-background shadow-sm" : "text-muted-foreground")}
              >
                {BILLING_CYCLE_LABELS[c]}
                {c === "anual" ? " (2 meses grátis)" : ""}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {o.plans.length === 0 ? <EmptyState title="Nenhum plano disponível" description="Fale com o suporte." /> : null}
        <div className="grid gap-4 md:grid-cols-3">
          {o.plans.map((plan) => {
            const current = plan.code === o.entitlements.plan.code && o.entitlements.status !== "trialing" && o.entitlements.status !== "canceled";
            const price = cycle === "anual" ? plan.priceYearlyCents : plan.priceMonthlyCents;
            return (
              <div key={plan.id} className={cn("flex flex-col gap-3 rounded-xl border p-4", current && "border-primary ring-1 ring-primary")}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold">{plan.name}</p>
                  {current ? <Badge>Plano atual</Badge> : null}
                </div>
                <p>
                  <span className="text-2xl font-semibold">{brl(price)}</span>
                  <span className="text-sm text-muted-foreground">/{cycle === "anual" ? "ano" : "mês"}</span>
                </p>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
                <ul className="space-y-1 text-sm">
                  {LIMIT_KEYS.map((k) => (
                    <li key={k} className="flex gap-2">
                      {plan.limits[k] === 0 ? <Minus className="mt-0.5 size-4 text-muted-foreground" aria-hidden /> : <Check className="mt-0.5 size-4 text-emerald-600" aria-hidden />}
                      <span>
                        {LIMIT_LABELS[k]}: {plan.limits[k] === 0 ? "não inclui" : formatLimit(plan.limits[k])}
                      </span>
                    </li>
                  ))}
                  {FEATURE_KEYS.map((f) => (
                    <li key={f} className={cn("flex gap-2", !plan.features[f] && "text-muted-foreground line-through")}>
                      {plan.features[f] ? <Check className="mt-0.5 size-4 text-emerald-600" aria-hidden /> : <Minus className="mt-0.5 size-4" aria-hidden />}
                      <span>{FEATURE_LABELS[f]}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  {subscribing ? (
                    <Button className="w-full" variant="outline" disabled={!o.canManage || current} loading={portal.isPending} onClick={() => portal.mutate()}>
                      {current ? "Plano atual" : "Trocar pelo portal"}
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      disabled={!o.canManage}
                      loading={checkout.isPending && checkout.variables.code === plan.code}
                      onClick={() => checkout.mutate(plan)}
                    >
                      Assinar {plan.name}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {!subscribing ? (
          <div className="flex max-w-sm items-end gap-2">
            <div className="flex-1 space-y-1">
              <label htmlFor="cupom" className="text-sm font-medium">
                Cupom (opcional)
              </label>
              <Input id="cupom" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="EX: BEMVINDO" maxLength={40} />
            </div>
          </div>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Assinaturas aceitam cartão de crédito e boleto. O Pix recorrente (Pix Automático) não está disponível no Stripe para empresas
          sediadas no Brasil.
        </p>
      </CardContent>
    </Card>
  );
}

function InvoicesCard({ o }: { o: BillingOverviewDto }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Faturas</CardTitle>
      </CardHeader>
      <CardContent>
        {o.invoices.length === 0 ? (
          <EmptyState title="Nenhuma fatura ainda" description="As faturas aparecem aqui depois da primeira cobrança." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Número</TableHead>
                <TableHead>Período</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {o.invoices.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell>{shortDate(inv.createdAt)}</TableCell>
                  <TableCell className="font-mono text-xs">{inv.number ?? "—"}</TableCell>
                  <TableCell>
                    {shortDate(inv.periodStart)} a {shortDate(inv.periodEnd)}
                  </TableCell>
                  <TableCell>{brl(inv.status === "paid" ? inv.amountPaidCents : inv.amountDueCents)}</TableCell>
                  <TableCell>
                    <Badge variant={inv.status === "paid" ? "success" : inv.status === "open" ? "warning" : "secondary"} title={inv.failureReason ?? undefined}>
                      {inv.status === "paid" ? "Paga" : inv.status === "open" ? "Em aberto" : inv.status === "void" ? "Anulada" : inv.status === "uncollectible" ? "Não cobrável" : inv.status}
                    </Badge>
                    {inv.failureReason && inv.status !== "paid" ? <p className="mt-1 text-xs text-muted-foreground">{inv.failureReason}</p> : null}
                  </TableCell>
                  <TableCell className="text-right">
                    {inv.hostedInvoiceUrl ? (
                      <Button asChild size="sm" variant="ghost">
                        <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                          Ver <ExternalLink />
                        </a>
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
